import { useCallback, useEffect, useRef } from 'react'

import { NullableType } from '@/models'

export type ResumeInactivityCallback = (startOver?: boolean) => void

export interface UseInactivityCheckProps {
  enabled: boolean
  inactivityDelay?: number
}

export const useInactivityCheck = (
  onInactivityCallback: (resumeInactivityTracking: ResumeInactivityCallback) => void,
  { enabled, inactivityDelay = 30000 }: UseInactivityCheckProps,
) => {
  const callbackRef = useRef(onInactivityCallback)
  callbackRef.current = onInactivityCallback

  const inactivityTimerRef = useRef<NullableType<ReturnType<typeof setTimeout>>>(null)
  const isTrackingRef = useRef(true)

  const clearTimeout = useCallback(() => {
    if (!inactivityTimerRef.current) return
    window.clearTimeout(inactivityTimerRef.current)
    inactivityTimerRef.current = null
  }, [])

  const startTimeout = useCallback(
    function scheduleTimeout() {
      if (!isTrackingRef.current) return

      clearTimeout()
      inactivityTimerRef.current = setTimeout(() => {
        isTrackingRef.current = false
        clearTimeout()

        const resumeInactivityTracking: ResumeInactivityCallback = (startOver = true) => {
          if (isTrackingRef.current) return
          isTrackingRef.current = true
          startOver && scheduleTimeout()
        }

        callbackRef.current(resumeInactivityTracking)
      }, inactivityDelay)
    },
    [clearTimeout, inactivityDelay],
  )

  useEffect(() => {
    if (!enabled) return clearTimeout()

    isTrackingRef.current = true
    startTimeout()

    window.addEventListener('mousemove', startTimeout, { passive: true })
    window.addEventListener('mousedown', startTimeout, { passive: true })
    window.addEventListener('keypress', startTimeout, { passive: true })
    window.addEventListener('touchstart', startTimeout, { passive: true })
    window.addEventListener('scroll', startTimeout, { passive: true })
    window.addEventListener('focus', startTimeout, { passive: true })
    window.addEventListener('blur', clearTimeout, { passive: true })

    return () => {
      isTrackingRef.current = true
      clearTimeout()

      window.removeEventListener('mousemove', startTimeout)
      window.removeEventListener('mousedown', startTimeout)
      window.removeEventListener('keypress', startTimeout)
      window.removeEventListener('touchstart', startTimeout)
      window.removeEventListener('scroll', startTimeout)
      window.removeEventListener('focus', startTimeout)
      window.removeEventListener('blur', clearTimeout)
    }
  }, [clearTimeout, startTimeout, enabled])
}
